import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { FileText, Receipt, Building2, UtensilsCrossed, ArrowRight } from "lucide-react";

const ICON_MAP = { FileText, Receipt, Building2, UtensilsCrossed };

function ServiceCard({ svc }) {
  const Icon = ICON_MAP[svc.icon] || FileText;
  const sortedPlans = useMemo(() => {
    return [...(svc.plans || [])].sort(
      (a, b) => (a.duration_years || 0) - (b.duration_years || 0) || a.price - b.price
    );
  }, [svc.plans]);
  const hasPlans = sortedPlans.length > 0;
  const isValidity = svc.pricing_type === "validity" || sortedPlans.some((p) => Number(p.duration_years) > 0);
  const defaultPlan = sortedPlans.find((p) => p.popular) || sortedPlans[0];
  const [selectedCode, setSelectedCode] = useState(defaultPlan?.code || null);
  const active = hasPlans ? sortedPlans.find((p) => p.code === selectedCode) || defaultPlan : null;
  const price = active ? active.price : svc.price;
  const applyHref = hasPlans && active ? `/apply/${svc.slug}?plan=${active.code}` : `/apply/${svc.slug}`;

  return (
    <div className="card-crisp rounded-xl p-6 flex flex-col" data-testid={`svc-card-${svc.slug}`}>
      <div className="flex items-center justify-between">
        <div className="w-12 h-12 rounded-lg bg-blue-50 text-[#1E3A8A] flex items-center justify-center">
          <Icon className="w-5 h-5" />
        </div>
        <span className="status-badge badge-info">{svc.processing_time}</span>
      </div>
      <div className="mt-4 font-display font-semibold text-xl text-[#0A192F]">{svc.name}</div>
      <div className="mt-1.5 text-sm text-slate-600 leading-relaxed">{svc.short_description}</div>

      <ul className="mt-4 space-y-1.5 text-sm text-slate-600">
        {(svc.features || []).slice(0, 3).map((f) => (
          <li key={f} className="flex gap-2"><span className="text-emerald-600">✓</span> {f}</li>
        ))}
      </ul>

      {/* Plan picker — pills OR dropdown based on admin setting */}
      {hasPlans && sortedPlans.length > 1 && (
        <div className="mt-4 pt-4 border-t border-slate-100" data-testid={`svc-plan-picker-${svc.slug}`}>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Choose Your Plan</div>
          {(svc.plan_display_style || "radio") === "dropdown" ? (
            <select
              value={active?.code || ""}
              onChange={(e) => setSelectedCode(e.target.value)}
              className="w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-xs"
              data-testid={`svc-${svc.slug}-plan-select`}
            >
              {sortedPlans.map((p) => (
                <option key={p.code} value={p.code}>
                  {isValidity && p.duration_years
                    ? `${p.duration_years} Yr${p.duration_years > 1 ? "s" : ""}`
                    : p.name} — ₹{p.price}
                </option>
              ))}
            </select>
          ) : (
            <div className="flex flex-wrap gap-1.5" data-testid={`svc-year-picker-${svc.slug}`}>
              {sortedPlans.map((p) => {
                const isActive = p.code === active?.code;
                const label = isValidity && p.duration_years
                  ? `${p.duration_years} Yr${p.duration_years > 1 ? "s" : ""}`
                  : p.name;
                return (
                  <button
                    key={p.code}
                    type="button"
                    onClick={() => setSelectedCode(p.code)}
                    className={`relative px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                      isActive
                        ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                        : "bg-white text-slate-700 border-slate-300 hover:border-[#1E3A8A]"
                    }`}
                    data-testid={`svc-${svc.slug}-year-${p.code}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="mt-auto pt-4 border-t border-slate-100 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">
            {hasPlans && active ? active.name : "Total fee"}
          </div>
          <div className="font-bold text-[#0A192F] text-lg" data-testid={`svc-${svc.slug}-price`}>₹{price}</div>
          {svc.pricing_note && (
            <div className="text-[9px] text-slate-500 leading-tight max-w-[160px]" data-testid={`svc-${svc.slug}-note`}>{svc.pricing_note}</div>
          )}
        </div>
        <div className="flex gap-2 shrink-0">
          <Link to={`/service/${svc.slug}`}>
            <Button variant="outline" size="sm" data-testid={`svc-view-${svc.slug}`}>Details</Button>
          </Link>
          <Link to={applyHref}>
            <Button size="sm" className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid={`svc-apply-${svc.slug}`}>
              Apply <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/services").then((r) => { setServices(r.data.services); setLoading(false); });
  }, []);

  return (
    <PublicLayout>
      <section className="gradient-radial-blue">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="section-label">All Services</div>
          <h1 className="mt-2 font-display font-bold text-4xl sm:text-5xl text-[#0A192F]">Choose the filing you need</h1>
          <p className="mt-3 text-slate-600 max-w-2xl">Transparent pricing. Expert-supported. Delivered on schedule.</p>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-slate-500">Loading services…</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((s) => <ServiceCard key={s.slug} svc={s} />)}
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
